import type { AircraftCalloutKind, AlertRuleSpec, AlertTier, BroadcastPreset } from './types.ts';
import { M_PER_FT, M_PER_NM } from './geo.ts';

/**
 * Callout grammar (spec §7.2): `[who], [clock], [distance], [trend/event].` — fixed order, short,
 * and written the way a TTS voice should say it ("two o'clock", "one point five miles",
 * "six hundred feet", "thirty seconds"). Pure; shared by the evaluator, web and the native app.
 *
 * Examples:
 *   calloutFor({ who: 'Heron', clock: 2, distanceM: 1852, trend: 'inbound' })  → "Heron, two o'clock, one mile, inbound."
 *   calloutFor({ who: 'Heron', trend: 'overhead' })                            → "Heron, overhead."
 *   calloutFor({ who: 'Transition 2', inSec: 10 })                                 → "Transition two, ten seconds."
 *   calloutFor({ who: 'Traffic', clock: 10, event: 'low, in your frame' })         → "Traffic, ten o'clock, low, in your frame."
 */

// ───────────────────────────── spoken numbers ─────────────────────────────

const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen',
  'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

/** Non-negative integer in words: 0 → "zero", 45 → "forty-five", 1200 → "one thousand two hundred". */
export function numberToWords(n: number): string {
  if (!Number.isFinite(n)) return '';
  const neg = n < 0;
  let v = Math.round(Math.abs(n));
  const parts: string[] = [];
  const under1000 = (x: number): string => {
    const out: string[] = [];
    if (x >= 100) {
      out.push(`${ONES[Math.floor(x / 100)]} hundred`);
      x %= 100;
    }
    if (x >= 20) out.push(x % 10 ? `${TENS[Math.floor(x / 10)]}-${ONES[x % 10]}` : TENS[Math.floor(x / 10)]!);
    else if (x > 0 || out.length === 0) out.push(ONES[x]!);
    return out.join(' ');
  };
  if (v === 0) return 'zero';
  if (v >= 1_000_000) {
    parts.push(`${under1000(Math.floor(v / 1_000_000))} million`);
    v %= 1_000_000;
  }
  if (v >= 1000) {
    parts.push(`${under1000(Math.floor(v / 1000))} thousand`);
    v %= 1000;
  }
  if (v > 0) parts.push(under1000(v));
  return (neg ? 'minus ' : '') + parts.join(' ');
}

/** Digits read one by one, the aviation way: "310" → "three one zero". */
export function digitsToWords(s: string): string {
  return [...s].filter((c) => /\d/.test(c)).map((c) => ONES[Number(c)]!).join(' ');
}

/** "two o'clock". */
export function spokenClock(clock: number): string {
  const c = ((Math.round(clock) - 1) % 12 + 12) % 12 + 1;
  return `${numberToWords(c)} o'clock`;
}

/**
 * Distance the way a TTS voice should say it, with the glance-card threshold (below 6,000 ft →
 * feet, else nautical miles, read as "miles"):
 *   < 1,000 ft → nearest 100 ft ("six hundred feet"); < 6,000 ft → nearest 500 ft ("two thousand five hundred feet");
 *   < 10 NM → nearest 0.5 NM ("one mile", "one point five miles"); else whole miles.
 */
export function spokenDistance(m: number): string {
  if (!Number.isFinite(m)) return '';
  const ft = Math.abs(m) / M_PER_FT;
  if (ft < DISTANCE_FEET_BELOW_FT) {
    const r = ft < 1000 ? Math.max(100, Math.round(ft / 100) * 100) : Math.round(ft / 500) * 500;
    return `${numberToWords(r)} feet`;
  }
  const nm = Math.abs(m) / M_PER_NM;
  const r = nm < 10 ? Math.max(1, Math.round(nm * 2) / 2) : Math.round(nm);
  const whole = Math.floor(r);
  const half = r - whole >= 0.5;
  const words = half ? `${numberToWords(whole)} point five` : numberToWords(whole);
  return `${words} ${r === 1 ? 'mile' : 'miles'}`;
}

/** Countdown the way a voice says it: 10 → "ten seconds", 60 → "one minute", 90 → "one minute thirty", 150 → "two minutes thirty". */
export function spokenDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${numberToWords(s)} ${s === 1 ? 'second' : 'seconds'}`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  const head = `${numberToWords(m)} ${m === 1 ? 'minute' : 'minutes'}`;
  if (r === 0 || m >= 10) return head;
  return `${head} ${numberToWords(Math.round(r / 5) * 5 || r)}`;
}

/** Glance-card rule, shared with the spoken form: below this many feet the distance is given in feet. */
export const DISTANCE_FEET_BELOW_FT = 6000;

/**
 * Speakable name from a label: numbers become words, tail/serial tokens are kept only when asked.
 * "HERON H01" → "Heron"; "Pass 2 — 500 ft" → "Pass two"; "CAM SITE B" → "Cam site B".
 */
export function spokenName(label: string, opts: { keepSerial?: boolean } = {}): string {
  const head = label.split(/\s+[—–-]\s+|[·|(]/)[0]!.trim();
  const words = head.split(/\s+/).filter(Boolean);
  const out: string[] = [];
  for (const raw of words) {
    // Tail numbers / serials (T01, N555VX) are hard to hear and rarely disambiguate on a shoot.
    if (!opts.keepSerial && out.length > 0 && /^[A-Z]{1,2}\d{2,}[A-Z]*$/i.test(raw)) continue;
    const unit = SPOKEN_UNITS[raw];
    if (unit) {
      out.push(unit);
      continue;
    }
    let w = raw;
    let deg = false;
    if (w.endsWith('°')) {
      deg = true;
      w = w.slice(0, -1);
    }
    const num = /^([+#]?)(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?$/.exec(w);
    if (num) {
      const int = numberToWords(Number(num[2]!.replace(/,/g, '')));
      const frac = num[3] ? ` point ${digitsToWords(num[3])}` : '';
      out.push(`${num[1] === '+' ? 'plus ' : ''}${int}${frac}${deg ? ' degrees' : ''}`);
      continue;
    }
    if (/^[A-Z]$/i.test(w)) {
      out.push(w.toUpperCase());
      continue;
    }
    // Upper-case tokens without vowels are acronyms: spell them ("RTB" → "R T B").
    if (/^[B-DF-HJ-NP-TV-XZ]{2,5}$/.test(w)) {
      out.push([...w].join(' '));
      continue;
    }
    out.push(w.toLowerCase());
  }
  const s = out.join(' ');
  return s ? s[0]!.toUpperCase() + s.slice(1) : '';
}

const SPOKEN_UNITS: Record<string, string> = { NM: 'miles', nm: 'miles', ft: 'feet', FT: 'feet', kt: 'knots', KT: 'knots', AGL: 'A G L' };

// ───────────────────────────── grammar ─────────────────────────────

export type CalloutTrend = 'inbound' | 'outbound' | 'overhead' | 'holding';

export interface CalloutInput {
  /** Aircraft, run-sheet item or source — raw labels are passed through `spokenName`. */
  who: string;
  /** Clock position 1–12 relative to the listener's facing; omitted when unknown. */
  clock?: number | null;
  /** Used only when no clock is known: compass word ("northeast"). */
  compass?: string | null;
  distanceM?: number | null;
  trend?: CalloutTrend | null;
  /** Free event text in the last slot ("in your frame", "takeoff", "closest in thirty seconds"). */
  event?: string | null;
  /** Countdown in seconds (run-sheet / scheduled events): "ten seconds". */
  inSec?: number | null;
  /** Say "scheduled" (spec §18: position unknown → schedule-based callouts say so). */
  scheduled?: boolean;
  /** Keep tail tokens in `who` ("Heron H zero one"). */
  keepSerial?: boolean;
}

/** One callout in the fixed grammar. Overhead drops clock and distance ("Heron, overhead."). */
export function calloutFor(input: CalloutInput): string {
  const parts: string[] = [];
  const who = spokenName(input.who, { keepSerial: input.keepSerial });
  if (who) parts.push(who);
  const overhead = input.trend === 'overhead';
  if (!overhead) {
    if (input.clock != null && Number.isFinite(input.clock)) parts.push(spokenClock(input.clock));
    else if (input.compass) parts.push(input.compass);
    if (input.distanceM != null && Number.isFinite(input.distanceM)) parts.push(spokenDistance(input.distanceM));
  }
  const tail: string[] = [];
  if (input.trend) tail.push(input.trend);
  if (input.event) tail.push(input.event);
  if (input.scheduled) tail.push('scheduled');
  if (input.inSec != null && Number.isFinite(input.inSec)) tail.push(spokenDuration(input.inSec));
  parts.push(...tail);
  const text = parts.filter(Boolean).join(', ');
  return text ? `${text}.` : '';
}

const COMPASS_WORDS: Record<string, string> = {
  N: 'north', NNE: 'north-northeast', NE: 'northeast', ENE: 'east-northeast', E: 'east', ESE: 'east-southeast', SE: 'southeast',
  SSE: 'south-southeast', S: 'south', SSW: 'south-southwest', SW: 'southwest', WSW: 'west-southwest', W: 'west', WNW: 'west-northwest',
  NW: 'northwest', NNW: 'north-northwest',
};

/** 8-point compass word for callouts ("northeast"). */
export function compassWord(deg: number): string {
  const pts = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const i = Math.round((((deg % 360) + 360) % 360) / 45) % 8;
  return COMPASS_WORDS[pts[i]!]!;
}

// ───────────────────────────── spoken updates (server announcer) ─────────────────────────────

/** A registration / tail number token: "N555VX", "G-ABCD" is not one (hyphenated labels keep spokenName). */
const TAIL_RE = /^[A-Z]{1,2}\d{1,5}[A-Z]{0,2}$/i;

/**
 * Speakable aircraft name for spoken updates. A label that IS a tail number is spelled the aviation
 * way ("N555VX" → "N five five five V X"); a name keeps `spokenName` ("HERON H01" → "Heron").
 */
export function spokenAircraftName(label: string): string {
  const first = label.trim().split(/\s+/)[0] ?? '';
  if (TAIL_RE.test(first) && /\d/.test(first)) {
    return [...first.toUpperCase()].map((c) => (/\d/.test(c) ? ONES[Number(c)]! : c)).join(' ');
  }
  return spokenName(label) || 'Aircraft';
}

export interface AircraftCalloutInput {
  kind: AircraftCalloutKind;
  /** Aircraft label as configured ("N555VX", "Heron"). */
  label: string;
  /** Listener → aircraft distance (m). For 'range' pass the ring crossed. */
  distanceM?: number | null;
  /** Listener → aircraft bearing (deg true), said as a compass word. */
  bearingDeg?: number | null;
  trend?: 'inbound' | 'outbound' | null;
}

/**
 * The text of one spoken update (callout grammar; never prohibited words):
 *   takeoff   "N five five five V X has taken off."
 *   range     "Heron, northeast, two miles, inbound."
 *   closest   "Heron, northeast, one mile, closest now."
 *   visible   "Heron, northeast, two miles, in view."   hidden  "Heron, behind terrain."
 *   data_lost "Heron, position data lost."              periodic "Heron, south, three miles, outbound."
 */
export function aircraftCalloutText(i: AircraftCalloutInput): string {
  const who = spokenAircraftName(i.label);
  const compass = i.bearingDeg != null && Number.isFinite(i.bearingDeg) ? compassWord(i.bearingDeg) : null;
  const where = { who, compass, distanceM: i.distanceM ?? null };
  switch (i.kind) {
    case 'takeoff':
      return `${who} has taken off.`;
    case 'landing':
      return `${who} has landed.`;
    case 'data_lost':
      return `${who}, position data lost.`;
    case 'data_back':
      return `${who}, position data back.`;
    case 'hidden':
      return calloutFor({ who, event: 'behind terrain' });
    case 'visible':
      return calloutFor({ ...where, event: 'in view' });
    case 'closest':
      return calloutFor({ ...where, event: 'closest now' });
    case 'range':
    case 'periodic':
      return calloutFor({ ...where, trend: i.trend ?? null });
  }
}

// ───────────────────────────── tiers ─────────────────────────────

/** Alert tier per rule type (spec §7.1). */
export function alertTierForRule(type: AlertRuleSpec['type'] | null | undefined): AlertTier {
  switch (type) {
    case 'proximity':
    case 'eta':
    case 'cpa':
    case 'phase_change':
    case 'runsheet_due':
      return 2;
    default:
      return 3; // sun, weather_change, stale_data, separation, crew_off_position
  }
}

/**
 * Every broadcast preset is tier 2 (product decision: content tool, no critical tier — nothing
 * produces tier 1, overrides mute, or requires acknowledgement).
 */
export function broadcastTier(_preset: BroadcastPreset): AlertTier {
  return 2;
}

/** Stored / shown text per preset (labels and spoken lines: crewComms.ts `BROADCAST_CALLS`). */
export const BROADCAST_PRESET_TEXT: Record<BroadcastPreset, string> = {
  ENGINES_START: 'ENGINES START',
  TAXIING: 'TAXIING',
  TAKEOFF: 'WHEELS UP',
  ON_STATION: 'ON STATION',
  STARTING_PASS: 'STARTING PASS',
  PASS_COMPLETE: 'PASS COMPLETE',
  RTB: 'RETURNING',
  LANDING: 'LANDING',
  SHUTDOWN: 'SHUTDOWN',
  HOLD: 'HOLD',
  GO: 'GO',
  RESET: 'RESET',
  NEXT_PASS: 'NEXT PASS',
  REPOSITION: 'REPOSITION',
  WRAP: 'WRAP',
  CUSTOM: '',
};

/**
 * "Hold. From Jake." — the broadcast's Alert `callout` (human text, exempt from the wording lint;
 * shown with the sender). Kept byte-for-byte with the native golden file; what listeners actually
 * hear is crewComms.ts `broadcastSpokenLine`.
 */
export function broadcastCallout(preset: BroadcastPreset, text: string, fromName: string): string {
  const body = (preset === 'CUSTOM' ? text : BROADCAST_PRESET_TEXT[preset]).trim().replace(/[.!?]+$/, '');
  const spoken = body ? body[0]!.toUpperCase() + body.slice(1).toLowerCase() : '';
  const from = spokenName(fromName, { keepSerial: true });
  return `${spoken}. From ${from}.`;
}

// ───────────────────────────── safety wording (spec §7.5) ─────────────────────────────

/**
 * Words generated alerts must never use: they imply the app provides separation or clearance.
 * Matched whole-word, case-insensitive. Human broadcast text is exempt (shown with the sender's name).
 */
export const PROHIBITED_ALERT_WORDS: readonly string[] = Object.freeze(['clear', 'safe', 'cleared', 'no traffic']);

const PROHIBITED_RE = new RegExp(`\\b(${PROHIBITED_ALERT_WORDS.map((w) => w.replace(/\s+/g, '\\s+')).join('|')})\\b`, 'gi');

/** Prohibited words found in `text` (lower-case, de-duplicated). Empty = passes. */
export function lintAlertText(text: string | null | undefined): string[] {
  if (!text) return [];
  const found = new Set<string>();
  for (const m of text.matchAll(PROHIBITED_RE)) found.add(m[0].toLowerCase().replace(/\s+/g, ' '));
  return [...found];
}
